import { Component, OnInit, Input, OnChanges, SimpleChanges } from '@angular/core';
import { PaletteService, generateContinuousLegendLabelsForRange } from 'map-wald';
import { LegendUtils, RangeStyle } from 'projects/map-wald/src/lib';
import { forkJoin } from 'rxjs';

export interface LegendConfig {
  colours?: string[],
  labels?: string[],
  shape?: string[],
  palette?: string,
  count?: number,
  reverse?: boolean,
  range?: [number, number],
  breakpoints?: number[],
  units?: string
}

@Component({
  selector: 'legend',
  template: `<div class="map-legend panel panel-group">
<ng-template #tooltipContent>
  <span [innerHtml]=helpText></span>
</ng-template>
  <strong class="legend-title">{{fullTitle}}
        <span *ngIf="helpText"
              [ngbTooltip]="tooltipContent"
              [placement]="tooltipPlacement"
              container="body">
          <i class="fa fa-info-circle"></i>
        </span>
  </strong>

  <p *ngIf="description&&descriptionPlacement==='above'"
        class="legend-description">
    {{description}}
</p>

  <div *ngIf="!imageURL">
    <div class="legend-body">
      <div class="legend-row"
          *ngFor="let colour of (legend?.colours||colours); let i=index">
        <div class="legend-colour" [ngClass]="legend?.shape?.[i]||legend?.shape?.[0]||markerClasses[i]||markerClasses[0]||''">
          <i class="legend-entry" [ngStyle]="{background:colour}"></i>
        </div>
        <div class="legend-label">
          <span [innerHTML]="generatedLabels[i]"></span>
        </div>
      </div>
    </div>
  </div>

  <div class="legend-image" *ngIf="imageURL">
    <img [src]="imageURL">
  </div>

  <p *ngIf="description&&descriptionPlacement==='below'"
        class="legend-description">
    {{description}}
  </p>

  <p *ngIf="attributionLink" class="legend-attribution"> Source: <a [href]="attributionLink">{{attribution || 'details'}}</a></p>
  <p *ngIf="attribution&&!attributionLink" class="legend-attribution">Source: {{attribution}}</p>
</div>
`, styles: [`
.map-legend{
  display:block;
  background: white;
}

.legend-body {
  display:table;
  line-height:15px;
}

.legend-row {
  display:table-row;
  padding:0;
}

.legend-description {
  font-size: 12px;
  margin-bottom: 0px;
}

.legend-colour{
  display:table-cell;
  padding:0px;
}

.legend-label{
  display:table-cell;
  padding:0px 4px 2px 2px;
  font-size:10px;
  vertical-align:middle;
}

.legend-attribution{
  font-size:10px;
}

.legend-image, .legend-image img {
  max-width:100%;
  max-height:40vh;
}

.legend-entry {
  float: left;
  width: 15px !important;
  height: 15px !important;
}

.legend-colour.circle i.legend-entry {
  border-radius:7px;
}
`]
})
export class LegendComponent implements OnInit, OnChanges {
  @Input() colours: Array<string> = ['red', 'white', 'blue'];
  @Input() labels: Array<string> = [];
  @Input() markerClasses: string[] = [];
  @Input() imageURL: string
  @Input() title = 'the title';
  @Input() mapUnits = '';
  @Input() helpText: string;
  @Input() tooltipPlacement = 'right';
  @Input() description: string;
  @Input() descriptionPlacement = 'above';
  @Input() attribution: string;
  @Input() attributionLink: string;
  fullTitle: string;
  _cbPalette:string
  _cbCount:number;
  _cbReverse:boolean;
  _cbRange:Array<number>;
  _legend:LegendConfig;
  @Input() set legend(l: LegendConfig) {
    this._legend = l;
    this._cbPalette = l.palette;
    this._cbCount = l.count;
    this._cbReverse = l.reverse;
    this._cbRange = l.range;
    this.generatePalette();
  }
  get legend(): LegendConfig {
    return this._legend;
  }

  @Input() set cbPalette(cbp:string){
    this._cbPalette = cbp;
    this.generatePalette();
  }

  get cbPalette():string{return this._cbPalette;}

  @Input() set cbCount(cbc:number){
    this._cbCount=cbc;
    this.generatePalette();
  }

  get cbCount():number{return this._cbCount;}

  @Input() set cbReverse(cbr:boolean){
    this._cbReverse=cbr;
    this.generatePalette();
  }

  get cbReverse():boolean{return this._cbReverse;}

  @Input() set cbRange(cbr:Array<number>){
    this._cbRange = cbr;
    this.generatePalette();
  }

  generatedLabels: string[] = [];

  constructor(private _palettes:PaletteService) { }
  ngOnChanges(changes: SimpleChanges): void {
    if(changes.labels||changes.legend){
      const altLabels = this.legend?.labels || this.labels;
      if(altLabels?.length){
        this.generatedLabels = altLabels;
      }
    }
    this.setTitle();
  }

  ngOnInit(): void {
  }

  generateLabels():Array<string>|null{
    if(this.legend?.breakpoints?.length){
      return LegendUtils.getLabels({breakpoints:this.legend.breakpoints} as RangeStyle<any>,undefined,this.legend?.units);
    }
    const count = this._cbCount || this.colours.length;
    if(!this._cbRange||!count){
      return null;
    }

    return generateContinuousLegendLabelsForRange(this._cbRange[0],this._cbRange[1],count);
  }

  generatePalette(){
    if(!this._cbPalette||!this._cbCount){
      return;
    }

    forkJoin([
      this._palettes.getPalette(this._cbPalette,this._cbReverse,this._cbCount),
      this._palettes.getLabels(this._cbPalette)
    ]).subscribe((data:string[][])=>{
        const [palette,labels] = data;
        this.colours = palette.slice().reverse();
        if(labels?.length){
          this.generatedLabels = labels.slice().reverse();
        } else {
          this.generatedLabels = this.legend?.labels || this.labels || this.generateLabels() || palette;
          if(!this.generateLabels?.length){
            this.generatedLabels = this.generateLabels();
          }
        }
    });
  }

  setTitle() {
    this.fullTitle = this.title || '';
    if (this.mapUnits) {
      if(this.fullTitle){
        this.fullTitle += ` (${this.mapUnits})`;
        this.fullTitle = this.fullTitle.replace(') (',', ');
      } else {
        this.fullTitle = this.mapUnits;
      }
    }
  }
}

export function makeColour(r: number, g: number, b: number, a?: number): string {
  a = (a === undefined) ? 1 : a;
  return `rgb(${r},${g},${b},${a})`;
}

